import { useState, useMemo, useEffect, useContext, useRef } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MoreHorizontal, ChevronRight, ChevronDown, Folder } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "./ui/button";
import { Dropdown } from "@/components/dropdown";
import { DashboardContext } from "./dashboard-layout";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";

export type SubmenuItem = {
  label?: string;
  id: string | number;
  name: string;
  href?: string;
  params?: any;
  dropdown?: SubmenuItem[];
  isNested?: boolean;
  children?: SubmenuItem[];
  isParent?: boolean;
  clickableParent?: boolean;
  icon?: React.ReactNode;
  className?: string;
  level?: number;
  parent?: { name: string; id: string | number; type?: string; href?: string };
};

interface SubmenuProps {
  itemsLoading?: boolean;
  title?: string;
  items: SubmenuItem[];
  placeholder?: string;
  isLoading?: boolean;
  createNew?: { label: string; href: string; params?: any }[];
  onClick?: (item: SubmenuItem, functionType?: string) => void;
  queryOptions?: any;
}

export function Submenu({
  itemsLoading = false,
  title,
  items = [],
  placeholder,
  isLoading = false,
  createNew = [],
  onClick,
  queryOptions,
}: SubmenuProps) {
  const [filter, setFilter] = useState("");
  const dashboardContext = useContext(DashboardContext);
  const location = useRouterState({ select: (s) => s.location });
  const isMobile = useIsMobile();
  const lastClosedPathRef = useRef<string | null>(null);

  // --- Handle manual submenu toggle events
  useEffect(() => {
    if (!dashboardContext?.submenu || !dashboardContext?.setSubmenu) return;

    const handleToggleSubmenu = (event: CustomEvent) => {
      const { route } = event.detail;
      const currentPath = location.pathname;

      const submenu = dashboardContext.submenu;
      if (!submenu) return;

      if (currentPath.startsWith(route)) {
        // Always reopen submenu when clicking the same sidebar section
        dashboardContext.setSubmenu({
          ...submenu,
          active: true,
          items: submenu.items ?? [],
        });
      }
    };

    window.addEventListener("toggleSubmenu", handleToggleSubmenu as EventListener);
    return () => {
      window.removeEventListener("toggleSubmenu", handleToggleSubmenu as EventListener);
    };
  }, [location.pathname, dashboardContext]);

  const query = queryOptions
    ? useQuery(queryOptions)
    : { data: undefined, isLoading: false, isError: false, error: null };

  useEffect(() => {
    if (query.isError) {
      const message =
        query.error instanceof Error ? query.error.message : "Failed to load submenu. Please try again later.";
      toast.error(message);
    }
  }, [query.isError, query.error]);

  const resolvedItems = useMemo(() => {
    if (queryOptions) {
      if (query.isLoading) return [];
      if (Array.isArray(query.data)) return query.data as SubmenuItem[];
      if (query.data && typeof query.data === "object" && "items" in query.data) {
        const dataWithItems = query.data as { items: SubmenuItem[] };
        return Array.isArray(dataWithItems.items) ? dataWithItems.items : [];
      }
      return [];
    }
    return Array.isArray(items) ? items : [];
  }, [query?.data, query?.isLoading, items, queryOptions]);

  const effectiveIsLoading = queryOptions ? query.isLoading : isLoading;

  const filterItemsRecursively = (items: SubmenuItem[], search: string): SubmenuItem[] => {
    if (!search) return items;
    return items
      .filter((item) => {
        if (!item || typeof item.name !== "string") return false;
        const matches = item.name.toLowerCase().includes(search);
        const childMatch = item.children ? filterItemsRecursively(item.children, search).length > 0 : false;
        return matches || childMatch;
      })
      .map((item) => ({
        ...item,
        children: item.children ? filterItemsRecursively(item.children, search) : [],
      }));
  };

  const filteredList = useMemo(() => {
    const search = filter.toLowerCase();
    return filterItemsRecursively(resolvedItems, search);
  }, [filter, resolvedItems]);

  return (
    <nav className="flex flex-col h-full overflow-hidden">
      <div className="sticky top-0 z-20 bg-background pb-4 space-y-4 shrink-0">
        {title ? (
          <div className="border-b pb-2 text-sm font-semibold outline-none ring-sidebar-ring focus-visible:ring-2">
            {title}
          </div>
        ) : null}

        <Input
          type="search"
          placeholder={placeholder || `Search${title ? " " + title?.toLowerCase() : ""}...`}
          onChange={(e) => setFilter(e.target.value)}
          disabled={effectiveIsLoading || itemsLoading}
        />

        {createNew.length > 0 &&
          createNew.map((item, idx) => (
            <Button
              key={idx}
              variant="outline"
              className="w-full"
              disabled={effectiveIsLoading || itemsLoading}
              onClick={() => {
                if (isMobile === true && dashboardContext?.submenu && dashboardContext.setSubmenu) {
                  dashboardContext.setSubmenu({
                    ...dashboardContext.submenu,
                    active: false,
                    items: dashboardContext.submenu.items ?? [],
                  });
                  lastClosedPathRef.current = location.pathname;
                }
              }}
            >
              <Link to={item.href} params={item.params}>
                {item.label}
              </Link>
            </Button>
          ))}
      </div>

      <div className="flex-1 overflow-y-auto space-y-1">
        {effectiveIsLoading || itemsLoading ? (
          <div className="flex flex-col gap-1">
            <div className="h-8 bg-muted animate-pulse rounded-md"></div>
            <div className="h-8 bg-muted animate-pulse rounded-md"></div>
            <div className="h-8 bg-muted animate-pulse rounded-md"></div>
          </div>
        ) : (
          <SubmenuItems
            items={filteredList}
            title={title}
            searchFilter={filter}
            onClick={
              onClick ? (dropdownItem, functionType) => onClick(dropdownItem as SubmenuItem, functionType) : undefined
            }
          />
        )}
      </div>
    </nav>
  );
}

function SubmenuItems({
  items,
  title,
  onClick,
  searchFilter,
  level = 0,
}: {
  items: SubmenuItem[];
  title?: string;
  onClick?: (item: SubmenuItem, functionType?: string) => void;
  searchFilter?: string;
  level?: number;
}) {
  const [expandedItems, setExpandedItems] = useState<Set<string | number>>(new Set());
  const dashboardContext = useContext(DashboardContext);
  const isMobile = useIsMobile();
  const location = useRouterState({ select: (s) => s.location });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => setHydrated(true), []);

  useEffect(() => {
    if (isMobile === true) {
      document.body.style.overflow = "auto";
      document.body.style.pointerEvents = "auto";
    }
  }, [location.pathname, isMobile]);

  // Auto-expand parent items when searching to show matching children
  useEffect(() => {
    if (searchFilter && searchFilter.trim()) {
      const autoExpandItems = new Set<string | number>();

      const findParentsWithMatchingChildren = (items: SubmenuItem[]) => {
        items.forEach((item) => {
          if (item.children && item.children.length > 0) {
            // Check if any children match the search
            const hasMatchingChild = item.children.some((child) =>
              child.name.toLowerCase().includes(searchFilter.toLowerCase())
            );
            if (hasMatchingChild) {
              autoExpandItems.add(item.id);
            }
            // Recursively check nested children
            findParentsWithMatchingChildren(item.children);
          }
        });
      };

      findParentsWithMatchingChildren(items);
      setExpandedItems((prev) => new Set([...prev, ...autoExpandItems]));
    }
  }, [searchFilter, items]);

  // Auto-expand parents of the active route so the tree stays open across reloads
  useEffect(() => {
    if (!hydrated) return;

    const autoExpandItems = new Set<string | number>();

    const hasActiveDescendant = (item: SubmenuItem): boolean => {
      const selfActive = isUrlActive(item.href) || isActive(item.href);

      if (item.children && item.children.length > 0) {
        let childHasActive = false;

        for (const child of item.children) {
          if (hasActiveDescendant(child)) {
            childHasActive = true;
          }
        }

        if (childHasActive && item.isParent) {
          autoExpandItems.add(item.id);
        }

        return selfActive || childHasActive;
      }

      return selfActive;
    };

    items.forEach((item) => hasActiveDescendant(item));

    if (autoExpandItems.size > 0) {
      setExpandedItems((prev) => new Set([...prev, ...autoExpandItems]));
    }
  }, [items, hydrated, location.pathname, location.search]);

  const isUrlActive = (href?: string) => {
    if (!href) return false;

    // Return false during SSR and initial client render to avoid hydration mismatch
    if (!hydrated) return false;

    // If it's a hash-only link, compare with the router's current hash
    if (href.startsWith("#")) {
      return location.hash === href.slice(1);
    }

    try {
      const url = new URL(href, window.location.origin);
      const hrefPathname = url.pathname;
      const hrefParams = new URLSearchParams(url.search);

      if (location.pathname !== hrefPathname) return false;

      if (url.search) {
        const currentSearchString = new URLSearchParams();
        if (location.search && typeof location.search === "object") {
          Object.entries(location.search).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              currentSearchString.set(key, String(value));
            }
          });
        }

        for (const [key, value] of hrefParams.entries()) {
          if (currentSearchString.get(key) !== value) return false;
        }
      }

      return true;
    } catch (e) {
      return location.pathname === href;
    }
  };

  const toggleExpanded = (id: string | number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleItemClick = (item: SubmenuItem, functionType?: string) => {
    if (onClick) onClick(item, functionType);
    if (isMobile === true && dashboardContext?.submenu && dashboardContext.setSubmenu && !functionType) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!dashboardContext.submenu) return;
          dashboardContext.setSubmenu({
            ...dashboardContext.submenu,
            active: false,
            items: dashboardContext.submenu.items ?? [],
          });
          document.body.style.overflow = "auto";
          document.body.style.pointerEvents = "auto";
        }, 200);
      });
    }
  };

  const isActive = (href?: string) => {
    const isActive = hydrated && href ? location.pathname === href || location.pathname.startsWith(href + "/") : false;
    return isActive;
  };

  const renderItem = (item: SubmenuItem, currentLevel: number) => {
    const paddingLeft = currentLevel * 16;
    const isExpanded = expandedItems.has(item.id);

    return (
      <div key={item.id}>
        {item.isParent && item.children && !item.clickableParent ? (
          <button
            onClick={() => toggleExpanded(item.id)}
            style={{ paddingLeft: paddingLeft + 12 }}
            className={`flex w-full items-center gap-2 rounded-md pr-3 py-2 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground`}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {item.icon ?? <Folder className="h-4 w-4" />}
            <span className="font-medium">{item.label || item.name}</span>
          </button>
        ) : item.isParent && item.children && item.clickableParent ? (
          <Link
            to={item.href}
            onClick={(e) => {
              // Prevent dropdown trigger clicks from navigating
              const target = e.target as HTMLElement;
              if (target.closest("[data-dropdown-trigger]")) {
                e.preventDefault();
                return;
              }
            }}
            style={{ paddingLeft: paddingLeft + 12 }}
            className={`flex items-center justify-between gap-2 rounded-md h-8 text-sm pr-3 py-2 ${
              isUrlActive(item.href)
                ? "bg-sidebar-primary text-sidebar-primary-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            }`}
          >
            <span className="truncate flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpanded(item.id);
                }}
                className="hover:bg-gray-600/10 h-8 w-8"
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
              {item.icon && item.icon}
              <span className="font-medium">{item.label || item.name}</span>
            </span>
            {item.dropdown && item.dropdown.length > 0 && (
              <div onClick={(e) => e.stopPropagation()} data-dropdown-trigger>
                <Dropdown
                  trigger={<MoreHorizontal />}
                  items={item.dropdown as any[]}
                  align="start"
                  side="right"
                  onClick={(dropdownItem, fnType) => handleItemClick(dropdownItem as SubmenuItem, fnType)}
                />
              </div>
            )}
          </Link>
        ) : item.href && item.href.startsWith("#") ? (
          <a
            href={item.href}
            onClick={(e) => {
              // Prevent dropdown trigger clicks from navigating
              const target = e.target as HTMLElement;
              if (target.closest("[data-dropdown-trigger]")) {
                e.preventDefault();
                return;
              }
              handleItemClick(item);
            }}
            aria-label={`Navigate to ${item.label || item.name}`}
            style={{ paddingLeft: `${paddingLeft + 12}px` }}
            className={`peer/menu-button w-full justify-between flex items-center gap-2 overflow-hidden rounded-md h-8 ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2 text-sm pr-3 py-2 ${
              isUrlActive(item.href)
                ? "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            }`}
          >
            <span className="truncate flex items-center gap-2">
              <div className="h-4 w-4 shrink-0" />
              {item.icon && item.icon}
              {item.label || item.name}
            </span>
            {item.dropdown && item.dropdown.length > 0 && (
              <div onClick={(e) => e.stopPropagation()} data-dropdown-trigger>
                <Dropdown
                  trigger={<MoreHorizontal />}
                  items={item.dropdown as any[]}
                  align="start"
                  side="right"
                  onClick={(dropdownItem, fnType) => handleItemClick(dropdownItem as SubmenuItem, fnType)}
                />
              </div>
            )}
          </a>
        ) : item.href && item.href.includes("?") ? (
          <Link
            to={item.href}
            onClick={(e) => {
              // Prevent dropdown trigger clicks from navigating
              const target = e.target as HTMLElement;
              if (target.closest("[data-dropdown-trigger]")) {
                e.preventDefault();
                return;
              }
              handleItemClick(item);
            }}
            aria-label={`Navigate to ${item.label || item.name}`}
            style={{ paddingLeft: `${paddingLeft + 12}px` }}
            className={`peer/menu-button w-full justify-between flex items-center gap-2 overflow-hidden rounded-md h-8 ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2 text-sm pr-3 py-2 ${
              isUrlActive(item.href)
                ? "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary hover:text-sidebar-primary-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            }`}
          >
            <span className="truncate flex items-center gap-2">
              <div className="h-4 w-4 shrink-0" />
              {item.icon && item.icon}
              {item.label || item.name}
            </span>
            {item.dropdown && item.dropdown.length > 0 && (
              <div onClick={(e) => e.stopPropagation()} data-dropdown-trigger>
                <Dropdown
                  trigger={<MoreHorizontal />}
                  items={item.dropdown as any[]}
                  align="start"
                  side="right"
                  onClick={(dropdownItem, fnType) => handleItemClick(dropdownItem as SubmenuItem, fnType)}
                />
              </div>
            )}
          </Link>
        ) : (
          <Link
            to={item.href}
            onClick={(e) => {
              // Prevent dropdown trigger clicks from navigating
              const target = e.target as HTMLElement;
              if (target.closest("[data-dropdown-trigger]")) {
                e.preventDefault();
                return;
              }
              handleItemClick(item);
            }}
            style={{ paddingLeft: paddingLeft + 12 }}
            className={`flex items-center justify-between gap-2 rounded-md h-8 text-sm pr-3 py-2 ${
              isActive(item.href)
                ? "bg-sidebar-primary text-sidebar-primary-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            }`}
          >
            <span className="truncate flex items-center gap-2">
              <div className="h-4 w-4 shrink-0" />
              {item.icon && item.icon}
              {item.label || item.name}
            </span>
            {item.dropdown && item.dropdown.length > 0 && (
              <div onClick={(e) => e.stopPropagation()} data-dropdown-trigger>
                <Dropdown
                  trigger={<MoreHorizontal />}
                  items={item.dropdown as any[]}
                  align="start"
                  side="right"
                  onClick={(dropdownItem, fnType) => handleItemClick(dropdownItem as SubmenuItem, fnType)}
                />
              </div>
            )}
          </Link>
        )}

        {item.children && item.children.length > 0 && (isExpanded || !item.isParent) && (
          <div>{item.children.map((c) => renderItem(c, currentLevel + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1 pr-2">
      {items.length ? (
        items.map((i) => renderItem(i, level))
      ) : (
        <div className="text-sm text-muted-foreground">
          {title ? `No ${title.toLowerCase()} available.` : "No items available."}
        </div>
      )}
    </div>
  );
}
